<template>
    <v-dialog :value="show" max-width="720" scrollable @input="(val) => !val && $emit('close')">
        <v-card class="fv-card">
            <v-card-title class="pb-0 align-start">
                <div class="fv-title">
                    <span class="fv-eyebrow">Feedback details</span>
                    <h2 class="fv-heading">{{ title }}</h2>
                </div>
                <v-btn icon class="ml-2" aria-label="Close" @click="$emit('close')"><v-icon color="primary">mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-card-text class="pt-4">
                <v-progress-linear v-if="loading" indeterminate class="mb-4" />
                <v-alert v-if="error" type="error" dense outlined>{{ error }}</v-alert>
                <template v-if="!loading && !error">
                    <div v-if="detail" class="fv-section">
                        <div class="d-flex flex-wrap mb-2">
                            <v-chip small label outlined :color="typeMeta.color" class="mr-2 mb-1"><v-icon small left>{{ typeMeta.icon }}</v-icon>{{ typeMeta.label }}</v-chip>
                            <v-chip small :color="statusMeta.color" text-color="white" class="mr-2 mb-1"><v-icon small left>{{ statusMeta.icon }}</v-icon>{{ statusMeta.label }}</v-chip>
                        </div>
                        <p class="fv-text mb-3">{{ detail.description }}</p>
                        <dl class="fv-facts">
                            <template v-if="pageText">
                                <dt>Page</dt>
                                <dd>{{ pageText }}</dd>
                            </template>
                            <template v-if="detail.suite">
                                <dt>Suite</dt>
                                <dd>{{ suiteLabel }}</dd>
                            </template>
                            <template v-if="detail.element_hint">
                                <dt>Where</dt>
                                <dd>{{ detail.element_hint }}</dd>
                            </template>
                            <dt>Submitted</dt>
                            <dd>{{ formatDate(detail.created_at) }}</dd>
                        </dl>
                    </div>

                    <div v-if="attachments.length" class="fv-section">
                        <h3 class="fv-h3">Screenshots</h3>
                        <FeedbackAttachments :item-id="item.id" :attachments="attachments" />
                    </div>

                    <div class="fv-section">
                        <h3 class="fv-h3">Votes by company</h3>
                        <v-simple-table v-if="companies.length" dense>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th class="text-left">Company</th>
                                        <th class="text-right">Votes</th>
                                        <th class="text-left">Users</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="company in companies" :key="company.company_id">
                                        <td>{{ company.company_name || "Company " + company.company_id }}</td>
                                        <td class="text-right font-weight-bold">{{ company.votes }}</td>
                                        <td>{{ company.users.join(", ") }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                        <p v-else class="grey--text mb-0">No votes yet.</p>
                    </div>

                    <div v-if="log.length" class="fv-section">
                        <h3 class="fv-h3">Status history</h3>
                        <v-list dense class="pa-0">
                            <v-list-item v-for="entry in log" :key="entry.id" class="px-0">
                                <v-list-item-content>
                                    <v-list-item-title>{{ statusLabel(entry.from_status) }} → {{ statusLabel(entry.to_status) }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ formatDate(entry.created_at) }}<span v-if="entry.note"> · {{ entry.note }}</span></v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </div>
                </template>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import FeedbackAttachments from "./FeedbackAttachments.vue"
    import { TYPE_META, STATUS_META, SUITE_LABELS } from "./feedbackMeta"

    export default {
        name: "FeedbackVotesByCompanyDialog",
        components: { FeedbackAttachments },
        props: {
            show: Boolean,
            item: Object
        },
        data() {
            return {
                loading: false,
                error: "",
                detail: null,
                companies: [],
                log: [],
                attachments: [],
                auth: "Basic " + window.btoa(process.env.VUE_APP_AUTH)
            }
        },
        computed: {
            title() {
                return this.item ? this.item.title : ""
            },
            typeMeta() {
                return TYPE_META[this.detail && this.detail.type] || TYPE_META.feature
            },
            statusMeta() {
                return STATUS_META[this.detail && this.detail.status] || STATUS_META.open
            },
            suiteLabel() {
                return SUITE_LABELS[this.detail && this.detail.suite] || (this.detail && this.detail.suite) || ""
            },
            pageText() {
                if (!this.detail) return ""
                return [this.detail.route_name, this.detail.route_path].filter(Boolean).join(" · ")
            }
        },
        watch: {
            show(val) {
                if (val && this.item) this.load()
            }
        },
        methods: {
            statusLabel(status) {
                return status && STATUS_META[status] ? STATUS_META[status].label : "New"
            },
            formatDate(value) {
                const date = new Date(value)
                return isNaN(date.getTime()) ? "" : date.toLocaleString()
            },
            load() {
                this.loading = true
                this.error = ""
                this.detail = null
                this.companies = []
                this.log = []
                this.attachments = []
                this.$http
                    .post(process.env.VUE_APP_APIURL + "/feedback/admin/items/" + this.item.id, { user: localStorage.getItem("user"), company_id: localStorage.getItem("company_id") }, { headers: { Authorization: this.auth } })
                    .then((response) => {
                        this.detail = response.body.item || null
                        this.companies = response.body.votes_by_company || []
                        this.log = response.body.log || []
                        this.attachments = response.body.attachments || []
                    })
                    .catch(() => {
                        this.error = "The details could not be loaded."
                    })
                    .finally(() => {
                        this.loading = false
                    })
            }
        }
    }
</script>

<style scoped>
    .fv-title {
        flex: 1;
        min-width: 0;
        word-break: break-word;
    }
    .fv-eyebrow {
        display: block;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--v-primary-base);
    }
    .fv-heading {
        font-family: "CalSans", sans-serif;
        font-weight: normal;
        font-size: 20px;
        line-height: 1.3;
    }
    .fv-section {
        margin-bottom: 24px;
    }
    .fv-h3 {
        margin-bottom: 8px;
        font-size: 14px;
        font-weight: 700;
    }
    .fv-text {
        white-space: pre-line;
        word-break: break-word;
        font-size: 14px;
        line-height: 1.5;
    }
    .fv-facts {
        display: grid;
        grid-template-columns: max-content 1fr;
        gap: 4px 16px;
        margin: 0;
        font-size: 13px;
    }
    .fv-facts dt {
        opacity: 0.6;
    }
    .fv-facts dd {
        margin: 0;
        word-break: break-word;
    }
</style>
